<script lang="ts" setup>
import { getApiErrorMessage } from '../libs/apiErrors'
import { nextTick, ref } from 'vue'
import { ILLUSTS_PER_PAGE } from '../../../libs/constants'
import { AlbumDetail, AlbumSummary, Illust } from '../../../libs/models'
import { useApi } from '../libs/api'
import { l } from '../libs/localization'
import illustUrl from '../libs/illustUrl'
import IllustModal from './IllustModal.vue'
import Loading from './Loading.vue'
import Modal from './Modal.vue'
import Stamps from './Stamps.vue'

const props = defineProps<{ album: AlbumSummary; manage?: boolean }>()
const emit = defineEmits<{
  close: []
  edit: []
  organize: []
  update: [album: AlbumSummary]
}>()

const api = useApi()
const currentAlbum = ref<AlbumSummary>(props.album)
const illusts = ref<Illust[]>([])
const selectedIllust = ref<Illust | null>(null)
const page = ref(0)
const isLoading = ref(false)
const completed = ref(false)
const scrollContainer = ref<HTMLElement>()
let savedScrollTop = 0

const fetchIllusts = () => {
  if (isLoading.value || completed.value) return
  isLoading.value = true
  api.get<AlbumDetail>(`/albums/${props.album.id}`, {
    params: { page: page.value + 1 }
  }).then(({ data }) => {
    page.value++
    currentAlbum.value = {
      id: data.id,
      user_id: data.user_id,
      name: data.name,
      private: data.private,
      illust_count: data.illust_count,
      cover_illust: data.cover_illust
    }
    emit('update', currentAlbum.value)
    illusts.value.push(...data.illusts)
    if (data.illusts.length < ILLUSTS_PER_PAGE) completed.value = true
  }).catch(error => {
    alert(getApiErrorMessage(error, 'アルバムを取得できませんでした'))
    if (!page.value) emit('close')
    else completed.value = true
  }).finally(() => {
    isLoading.value = false
  })
}

const onScroll = (event: Event) => {
  const element = event.target as HTMLElement
  if (element.scrollHeight - element.scrollTop - element.clientHeight <= 20) fetchIllusts()
}

const getIllustAuthor = (illust: Illust) => {
  if (illust.user_id === currentAlbum.value.user_id) return l('本人')
  return `by ${illust.user?.name || l('ユーザー{id}', { id: illust.user_id })}`
}

const openIllust = (illust: Illust) => {
  savedScrollTop = scrollContainer.value?.scrollTop || 0
  selectedIllust.value = illust
}

const closeIllust = () => {
  selectedIllust.value = null
  nextTick(() => {
    if (scrollContainer.value) scrollContainer.value.scrollTop = savedScrollTop
  })
}

const onDeleted = () => {
  if (!selectedIllust.value) return
  const deletedIllustId = selectedIllust.value.id
  illusts.value = illusts.value.filter(illust => illust.id !== deletedIllustId)
  currentAlbum.value = {
    ...currentAlbum.value,
    illust_count: Math.max(currentAlbum.value.illust_count - 1, 0),
    cover_illust: currentAlbum.value.cover_illust?.id === deletedIllustId
      ? (illusts.value[0] ? { id: illusts.value[0].id, user_id: illusts.value[0].user_id } : null)
      : currentAlbum.value.cover_illust
  }
  emit('update', currentAlbum.value)
  closeIllust()
}

fetchIllusts()
</script>

<template>
  <transition name="modal">
    <IllustModal
      v-if="selectedIllust"
      :key="`illust:${selectedIllust.id}`"
      :illust="selectedIllust"
      @deleted="onDeleted"
      @close="closeIllust"
    />
    <Modal v-else key="album" @close="emit('close')">
      <div class="ModalContent AlbumDetailContent">
        <div class="ModalTitle">{{ currentAlbum.name }}</div>
        <span v-if="manage" class="AlbumVisibility" :class="{ private: currentAlbum.private }">
          <span class="material-symbols-rounded" aria-hidden="true">{{ currentAlbum.private ? 'lock' : 'public' }}</span>
          {{ currentAlbum.private ? l('非公開（自分のみ）') : l('公開') }}
        </span>
        <div ref="scrollContainer" class="ScrollContainer" @scroll="onScroll">
          <ul v-if="illusts.length" class="IllustsList">
            <li v-for="illust in illusts" :key="illust.id" class="IllustsList-item" @click="openIllust(illust)">
              <div class="IllustTitle">
                『{{ illust.title }}』
                <small class="IllustAuthor" :class="{ owner: illust.user_id === currentAlbum.user_id }">
                  {{ getIllustAuthor(illust) }}
                </small>
                <span v-if="illust.comment_count" class="CommentCount">
                  <span class="material-symbols-rounded">comment</span> {{ illust.comment_count }}
                </span>
              </div>
              <div class="IllustImage"><img :src="illustUrl(illust.user_id, illust.id)" alt=""></div>
              <Stamps :illust="illust" :readonly="true" />
            </li>
          </ul>
          <Loading v-if="isLoading" />
          <div v-else-if="!illusts.length" class="EmptyState">{{ l('イラストがまだありません') }}</div>
        </div>
        <template v-if="manage">
          <button @click="emit('edit')"><span class="material-symbols-rounded" aria-hidden="true">edit</span>{{ l('アルバムを編集') }}</button>
          <button
            :class="{ disabled: currentAlbum.illust_count < 1 }"
            :disabled="currentAlbum.illust_count < 1"
            @click="emit('organize')"
          ><span class="material-symbols-rounded" aria-hidden="true">collections</span>{{ l('イラストを整理') }}</button>
        </template>
        <button class="cancel" @click="emit('close')">{{ l('閉じる') }}</button>
      </div>
    </Modal>
  </transition>
</template>

<style lang="scss" scoped>
.AlbumDetailContent {
  display: flex;
  flex-direction: column;
  width: 600px;
  max-width: calc(100vw - 30px);
  height: 100%;
  overflow: hidden;
  line-height: 1.5;
  > button { margin-top: 10px; }
}
.AlbumVisibility {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  margin-top: 4px;
  color: var(--green);
  font-size: 11px;
  &.private { color: var(--tx-color-light); }
  .material-symbols-rounded { font-size: 16px; }
}
.ScrollContainer { flex: 1; min-height: 0; overflow-y: auto; margin: 10px 0; }
.IllustsList {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  min-height: 101%;
  &::after { display: block; width: calc(33.333% - 6.666px); content: ''; }
}
.IllustsList-item {
  width: calc(33.333% - 6.666px);
  margin: 5px auto 15px;
  overflow: hidden;
  background: var(--bg-color);
  cursor: pointer;
  text-align: center;
}
.IllustTitle {
  font-size: 13px;
  font-weight: bold;
}
.IllustAuthor {
  color: var(--tx-color-light);
  font-size: 10px;
  font-weight: normal;
  &.owner {
    color: var(--green);
    font-weight: bold;
  }
}
.CommentCount {
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
  font-size: 12px;
  vertical-align: middle;
  .material-symbols-rounded { margin-right: 2px; font-size: 14px; }
}
.IllustImage {
  overflow: hidden;
  margin: 4px auto 2px;
  border-radius: 10px;
  img { display: block; width: 100%; }
}
::v-deep(.Stamps-list-item) {
  font-size: 12px;
  small { font-size: 11px; }
}
.EmptyState { padding: 40px 10px; color: var(--tx-color-light); font-size: 13px; text-align: center; }
</style>
